<section class="test-plans-page">
  <app-project-nav></app-project-nav>
  <app-page-header [title]="'testPlans.list.headline' | translate" [subtitle]="'testPlans.list.subtitle' | translate">
    <ng-container right>
      <app-stat-group [items]="stats()" />
      @if (!isReadOnly()) {
        <button type="button" class="test-plans-page__create-btn" (click)="onCreate()">
          <span class="test-plans-page__create-btn-icon" aria-hidden="true">＋</span>
          {{ 'testPlans.list.create' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <div class="test-plans-page__toolbar">
    <app-search-bar
      data-testid="testplan-search"
      [placeholder]="'testPlans.list.search' | translate"
      [value]="searchTerm()"
      (valueChange)="onSearchChange($event)"
    />
    <app-filter-dropdown
      name="assignee"
      [value]="assigneeFilterValue()"
      [placeholder]="'testPlans.list.filter.allAssignees' | translate"
      [options]="assigneeDropdownOptions()"
      (valueChange)="setAssigneeFilter($event)"
    />
    <!-- Rollout NEX-787: geteilter Ansichten-Baustein aus NEX-786, rechts neben den Filter-Chips
         (Design-Referenz page-testplaene.jsx:239-263, Bundle c7616651204). Ersetzt die vormals
         lokale, nur-localStorage-basierte app-saved-views-Komponente (NEX-803/804). -->
    <app-saved-views-menu
      data-testid="testplan-saved-views"
      [views]="savedViewsOptions()"
      [activeName]="activeSavedViewName()"
      [context]="'TEST_PLANS'"
      [projectId]="projectId"
      (viewSelected)="onSavedViewSelected($event)"
      (viewSaved)="onSavedViewSaved($event)"
      (viewDeleted)="onSavedViewDeleted($event)"
      (viewDeleteFailed)="onSavedViewDeleteFailed()"
    />
    @if (hasFilters()) {
      <app-reset-filter-button [label]="'testPlans.list.filter.reset' | translate" (resetClick)="resetFilters()" />
    }
    <div class="test-plans-page__toolbar-spacer"></div>
    <span class="test-plans-page__count" data-testid="testplan-counter">
      {{ resultCountKey() | translate: { count: total(), total: unfilteredTotal() } }}
    </span>
  </div>

  @if (savedViewsError()) {
    <p class="test-plans-page__hint test-plans-page__hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <p class="test-plans-page__hint">{{ 'testPlans.list.loading' | translate }}</p>
  } @else if (loadError()) {
    <p class="test-plans-page__hint">{{ 'testPlans.list.loadError' | translate }}</p>
  } @else if (filteredPlans().length === 0) {
    <p class="test-plans-page__hint">
      {{ (plans().length === 0 ? 'testPlans.list.empty' : 'testPlans.list.noResults') | translate }}
    </p>
  } @else {
    <div
      appListFocusScroll
      class="test-plans-table"
      role="table"
      [attr.aria-label]="'testPlans.list.tableAria' | translate"
    >
      <div class="test-plans-table__head" role="row">
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--id"
          role="columnheader"
          [attr.aria-sort]="ariaSort('publicId')"
          (click)="toggleSort('publicId')"
        >
          {{ 'testPlans.list.columns.id' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--title"
          role="columnheader"
          [attr.aria-sort]="ariaSort('title')"
          (click)="toggleSort('title')"
        >
          {{ 'testPlans.list.columns.title' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--release"
          role="columnheader"
          [attr.aria-sort]="ariaSort('release')"
          (click)="toggleSort('release')"
        >
          {{ 'testPlans.list.columns.release' | translate }}
        </button>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--assignee"
          role="columnheader"
          [attr.aria-sort]="ariaSort('assignee')"
          (click)="toggleSort('assignee')"
        >
          {{ 'testPlans.list.columns.assignee' | translate }}
        </button>
        <div class="test-plans-table__th test-plans-table__th--progress" role="columnheader">
          {{ 'testPlans.list.columns.progress' | translate }}
        </div>
        <button
          type="button"
          class="test-plans-table__th test-plans-table__th--due"
          role="columnheader"
          [attr.aria-sort]="ariaSort('dueDate')"
          (click)="toggleSort('dueDate')"
        >
          {{ 'testPlans.list.columns.due' | translate }}
        </button>
        <div class="test-plans-table__th test-plans-table__th--actions" role="columnheader">
          {{ 'testPlans.list.columns.actions' | translate }}
        </div>
      </div>

      @for (plan of pagedPlans(); track trackById($index, plan)) {
        <div
          class="test-plans-table__row"
          role="row"
          tabindex="0"
          [attr.aria-label]="plan.title"
          (click)="onOpenRow(plan)"
          (keydown.enter)="onRowKeydown($event, plan)"
          (keydown.space)="onRowKeydown($event, plan)"
        >
          <div class="test-plans-table__td test-plans-table__td--id" role="cell">
            @if (plan.publicId) {
              <app-code-id-chip [id]="plan.publicId" />
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--title" role="cell">
            <div class="test-plans-table__title">{{ plan.title }}</div>
            <div class="test-plans-table__meta">
              {{ plan.testCaseIds.length }} {{ 'testPlans.list.testcasesSuffix' | translate }}
            </div>
          </div>
          <div class="test-plans-table__td test-plans-table__td--release" role="cell">
            @if (plan.releaseId) {
              <span class="test-plans-table__release">{{ plan.releaseId }}</span>
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--assignee" role="cell">
            @if (plan.assigneeName) {
              <div class="test-plans-table__assignee">
                <app-avatar [name]="plan.assigneeName" [size]="26" />
                <span class="test-plans-table__assignee-name">{{ plan.assigneeName }}</span>
              </div>
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--progress" role="cell">
            <app-progress-bar
              variant="block"
              [ariaLabel]="'testPlans.list.progressAria' | translate: { percent: progressPercent(plan) }"
              [passed]="plan.progress.passed"
              [failed]="plan.progress.failed"
              [inProgress]="plan.progress.inProgress"
              [open]="plan.progress.open"
            />
            <div class="test-plans-table__progress-meta">
              {{ progressPercent(plan) }}% {{ 'testPlans.list.executedSuffix' | translate }} ·
              {{ plan.progress.passed }} {{ 'testPlans.list.passedSuffix' | translate }} · {{ plan.progress.failed }}
              {{ 'testPlans.list.failedSuffix' | translate }}
            </div>
          </div>
          <div class="test-plans-table__td test-plans-table__td--due" role="cell">
            @if (plan.dueDate) {
              {{ plan.dueDate | date: 'dd.MM.yyyy' }}
            } @else {
              <span class="test-plans-table__faint">—</span>
            }
          </div>
          <div class="test-plans-table__td test-plans-table__td--actions" role="cell">
            <button
              type="button"
              class="test-plans-table__action test-plans-table__action--accent"
              [attr.aria-label]="'testPlans.list.actions.report' | translate"
              (click)="onOpenReport(plan, $event)"
            >
              <mat-icon fontSet="material-icons-outlined" aria-hidden="true">shield</mat-icon>
            </button>
            @if (!isReadOnly()) {
              <button
                type="button"
                class="test-plans-table__action"
                [attr.aria-label]="'testPlans.list.actions.edit' | translate"
                (click)="onEdit(plan, $event)"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="test-plans-table__action test-plans-table__action--danger"
                [attr.aria-label]="'testPlans.list.actions.delete' | translate"
                (click)="onAskDelete(plan, $event)"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
        </div>
      }
    </div>

    <div class="tp-footer">
      <span class="tp-footer__range" role="status">
        {{ 'testPlans.footer.rangeLabel' | translate: { start: rangeStart(), end: rangeEnd(), total: total() } }}
      </span>
      <div class="tp-footer__pager">
        <label class="tp-footer__label" for="tp-page-size">{{ 'testPlans.footer.rowsPerPage' | translate }}</label>
        <select
          id="tp-page-size"
          class="tp-footer__select"
          (change)="onPageSizeChange(+pageSizeSelect.value)"
          #pageSizeSelect
          [attr.aria-label]="'testPlans.footer.rowsPerPageAriaLabel' | translate"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        @if (pageCount() > 1) {
          <div class="tp-footer__nav">
            <button
              type="button"
              class="tp-action-btn"
              [disabled]="pageIndex() === 0"
              [attr.aria-label]="'testPlans.footer.previousPage' | translate"
              (click)="onPageChange(pageIndex() - 1)"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
            </button>
            <button
              type="button"
              class="tp-action-btn"
              [disabled]="pageIndex() >= pageCount() - 1"
              [attr.aria-label]="'testPlans.footer.nextPage' | translate"
              (click)="onPageChange(pageIndex() + 1)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </div>
        }
      </div>
    </div>
  }

  @if (deleteError()) {
    <p class="test-plans-page__hint test-plans-page__hint--error">{{ deleteErrorKey() | translate }}</p>
  }

  <app-confirm-dialog
    [visible]="planToDelete() !== null"
    [title]="'testPlans.list.deleteDialog.title' | translate"
    [message]="
      planToDelete()
        ? ('testPlans.list.deleteDialog.message'
          | translate: { title: planToDelete()!.title, id: planToDelete()!.publicId })
        : ''
    "
    [confirmLabel]="'testPlans.list.deleteDialog.confirm' | translate"
    [cancelLabel]="'testPlans.list.deleteDialog.cancel' | translate"
    [busy]="deleting()"
    (confirmed)="onConfirmDelete()"
    (cancelled)="onCancelDelete()"
  />
</section>
